import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '@/lib/authFetch';
import { useToast } from '@/hooks/use-toast';

const REFRESH_INTERVAL = 5 * 60 * 1000; // Verifica a cada 5 minutos
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutos sem atividade = não renova (expira)

export const useAdminSession = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const lastActivityRef = useRef<number>(Date.now());
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Atualiza timestamp de última atividade
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Faz logout e redireciona
  const logout = useCallback((message?: string) => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    
    if (message) {
      toast({
        title: "Sessão Expirada",
        description: message,
        variant: "destructive",
      });
    }
    
    navigate('/admin/login');
  }, [navigate, toast]);

  // Renova o token. Só faz logout em 401 (sessão inválida). Erro de rede/502 não derruba a sessão.
  const refreshToken = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      logout();
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/refresh-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        logout("Sua sessão expirou. Faça login novamente.");
        return false;
      }

      if (!response.ok) {
        // 502, 503, etc.: não deslogar, próxima tentativa pode funcionar
        return false;
      }

      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem('admin_token', data.access_token);
      }
      return true;
    } catch {
      // Erro de rede / CORS / timeout: não deslogar (evita logout ao dar F5 com API instável)
      return false;
    }
  }, [logout]);

  // Verifica se precisa renovar o token
  const checkAndRefreshToken = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      logout();
      return;
    }

    const timeSinceActivity = Date.now() - lastActivityRef.current;
    
    // Se houve atividade nos últimos 25 minutos, renova o token
    if (timeSinceActivity < INACTIVITY_TIMEOUT) {
      await refreshToken();
    }
    // Sem atividade por muito tempo: não renova, token expira
  }, [refreshToken, logout]);

  // Configura listeners de atividade
  useEffect(() => {
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Inicia intervalo de verificação
    refreshIntervalRef.current = setInterval(checkAndRefreshToken, REFRESH_INTERVAL);

    // Renova imediatamente ao montar (se tiver token)
    const token = localStorage.getItem('admin_token');
    if (token) {
      // Pequeno delay para não conflitar com outras requisições iniciais
      setTimeout(() => refreshToken(), 2000);
    }

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [updateActivity, checkAndRefreshToken, refreshToken]);

  return { logout, refreshToken, updateActivity };
};

export default useAdminSession;

